import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useMemo } from 'react'
import { AccountMeResponse } from '~/api/accounts'
import { OffsetLimit, OffsetLimitPaginationResponse } from '~/api/common'
import { objectHookToContext } from '~/common/hookToContext'
import { useQueryProgressIndicator } from '~/common/react-query/queryProgressIndicator'
import { assertNotInReactContext, ignoreProviderMissingError, isWithinReactComponentOrHook } from '~/common/reactUtils'
import { Prettify } from '~/common/type-helpers'
import { useActionBarQueryValues } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.hook'
import { BulkStrategyQuery, ItemWithId } from '~/components/v2/ResultsLayout/BulkActionBar/BulkActionBar'
import { useFilterValues, useIsFilterConfigLoading } from '~/components/v2/ResultsLayout/Filters/FiltersProvider'
import { usePaginationOffsetLimit } from '~/components/v2/ResultsLayout/Pagination/ResultsPagination'

export type QueryOptsFn<TData extends ItemWithId, TParams extends Partial<OffsetLimit> = Partial<OffsetLimit>> = (
  queryParams: TParams,
  enabled?: boolean
) => UseQueryOptions<OffsetLimitPaginationResponse<TData>>

export type QueryOptsWithUserFn<TData extends number[], TParams extends Partial<OffsetLimit> = Partial<OffsetLimit>> = (
  queryParams: TParams,
  userAccount: AccountMeResponse
) => UseQueryOptions<TData>

/**
 * The internal provider uses `any` to avoid contravariance issues when accepting different
 * queryOpts signatures. defineListData's `useContext()` narrows the types back via assertion.
 * This is a pragmatic tradeoff — the type safety comes from the defineListData call site,
 * not from the context itself.
 */
export interface ListQueryResultProviderProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryOpts: QueryOptsFn<any, any>
  bulkSelectStrategyOpts?: BulkStrategyQuery
  isItemBulkActionable?: (item: any) => boolean
}

const useInternalListData = ({
  queryOpts,
  bulkSelectStrategyOpts,
  isItemBulkActionable
}: ListQueryResultProviderProps) => {
  const [filterValues] = ignoreProviderMissingError(useFilterValues)
  const [isFiltersLoading] = ignoreProviderMissingError(useIsFilterConfigLoading)
  const [actionBarValues] = ignoreProviderMissingError(useActionBarQueryValues)
  const [offsetLimit] = ignoreProviderMissingError(usePaginationOffsetLimit)

  const queryParams = useMemo(
    () => ({
      ...filterValues,
      ...actionBarValues,
      ...offsetLimit
    }),
    [filterValues, actionBarValues, offsetLimit]
  )

  const queryResult = useQuery(queryOpts(queryParams, !isFiltersLoading))

  useQueryProgressIndicator(queryResult.isFetching, {
    parentSelector: '[data-nprogress-bar-container]',
    height: 2
  })

  return {
    queryResult,
    queryParams,
    bulkSelectStrategyOpts,
    isItemBulkActionable
  }
}
const context = objectHookToContext(useInternalListData)

export const ListDataProvider = context[0]
const _useInternalListDataContext = context[1]

type ListDataContextValue = ReturnType<typeof useInternalListData>
type ListDataFallback = UseQueryResult<OffsetLimitPaginationResponse<any>>

export function useInternalListDataContext(options?: { fallback?: ListDataFallback }): ListDataContextValue
export function useInternalListDataContext(options: {
  fallback?: ListDataFallback
  optional: true
}): ListDataContextValue | undefined
export function useInternalListDataContext({
  fallback,
  optional = false
}: { fallback?: ListDataFallback; optional?: boolean } = {}): ListDataContextValue | undefined {
  // react-tracked only throws "Please use <Provider>" outside production. During a production
  // prerender it returns undefined instead, so ignoreProviderMissingError never catches anything and
  // its own fallback never fires. Resolve the fallback here against the (possibly absent) context so
  // behavior is identical in dev and prerender.
  const context = ignoreProviderMissingError(_useInternalListDataContext)[0]
  const resolved = context ?? (fallback ? ({ queryResult: fallback } as ListDataContextValue) : undefined)

  if (!resolved && !optional) {
    throw new Error(
      'ListDataProvider was not found. Wrap your component in <ListDataProvider> or pass a queryResult fallback.'
    )
  }

  return resolved
}

export const useInternalListDataQueryResult = ({ fallback }: { fallback?: ListDataFallback } = {}) =>
  useInternalListDataContext({ fallback, optional: true })?.queryResult

type MaybeHasUseValues = { useValues: () => any } | null

/**
 * Computes the full query params type by combining:
 *   1. OffsetLimit (offset, limit) — always present
 *   2. Filter values — inferred from the specific filters config
 *   3. Action bar values — inferred from the action bar config (search, view, ordering)
 *
 * This uses conditional types to extract the right value type from either
 * defineFilters (has useValues directly) or defineFiltersWithContext (has resolveConfig → useValues).
 */
export type QueryParams<TFiltersDef extends MaybeHasUseValues, TActionBarDef extends MaybeHasUseValues> = Prettify<
  OffsetLimit &
    (TFiltersDef extends { useValues: () => infer R } ? R : Record<string, never>) &
    (TActionBarDef extends { useValues: () => infer R } ? R : Record<string, never>)
>

type DefineListDataProps<
  TFiltersDef extends MaybeHasUseValues,
  TActionBarDef extends MaybeHasUseValues,
  TData extends ItemWithId,
  TParams extends Partial<OffsetLimit> = QueryParams<TFiltersDef, TActionBarDef>
> = {
  filtersDef: TFiltersDef
  actionBarDef: TActionBarDef
  queryOpts: QueryOptsFn<TData, TParams>
  bulkSelectStrategyOpts?: BulkStrategyQuery
  isItemBulkActionable?: (item: any) => boolean
}

/**
 * @agent-preferred
 * @agent-doc vndly/assets/js/components/v2/ResultsLayout/docs/GettingStarted.mdx
 * @agent-gotcha MUST be called outside the React component (module level).
 * @agent-gotcha Returns { queryOpts, useContext } — pass to ListLayout via listDataDef prop.
 */
export function defineListData<
  TFiltersDef extends MaybeHasUseValues,
  TActionBarDef extends MaybeHasUseValues,
  TData extends ItemWithId
>({
  // configs are only used for type inference
  filtersDef: _,
  actionBarDef: __,
  queryOpts,
  bulkSelectStrategyOpts,
  isItemBulkActionable
}: DefineListDataProps<TFiltersDef, TActionBarDef, TData>) {
  assertNotInReactContext()

  return {
    queryOpts,
    bulkSelectStrategyOpts,
    isItemBulkActionable,
    /**
     * Returns the typed context for this list. The internal provider is type-erased (uses `any`),
     * so we narrow the types back via assertion using the generics captured at definition time.
     * This is a pragmatic tradeoff — the type safety comes from the defineListData call site.
     */
    useContext: () =>
      useInternalListDataContext() as unknown as {
        queryResult: UseQueryResult<OffsetLimitPaginationResponse<TData>>
        queryParams: QueryParams<TFiltersDef, TActionBarDef>
      }
  }
}

/**
 * @agent-preferred
 * @agent-doc vndly/assets/js/components/v2/ResultsLayout/docs/GettingStarted.mdx
 * @agent-gotcha MUST be called outside the React component (module level).
 * @agent-gotcha Use over defineListData when listDataDef needs runtime context (api data, user role). Callback receives context; otherwise same shape and { queryOpts, useContext } return as defineListData.
 */
export function defineListDataWithContext<
  TContext,
  TFiltersDef extends MaybeHasUseValues,
  TActionBarDef extends MaybeHasUseValues,
  TData extends ItemWithId
>(listDataCallback: (context: TContext) => DefineListDataProps<TFiltersDef, TActionBarDef, TData>) {
  assertNotInReactContext()

  const useContext = () =>
    useInternalListDataContext() as unknown as {
      queryResult: UseQueryResult<OffsetLimitPaginationResponse<TData>>
      queryParams: QueryParams<TFiltersDef, TActionBarDef>
    }

  return {
    resolveConfig: (ctx: TContext) => {
      const { queryOpts, bulkSelectStrategyOpts, isItemBulkActionable } = listDataCallback(ctx)
      return {
        queryOpts,
        bulkSelectStrategyOpts,
        isItemBulkActionable,
        useContext
      }
    },
    // Return useContext statically as well
    useContext
  }
}
